import React, { useState, useEffect, useCallback } from "react";
import AppointmentService, {
  Appointment,
} from "../../services/appointmentService";
import colorTheme from "../common/color";
import { useAuth } from "../../hooks/useAuth";
import getIcon from "../../utils/Icons";
import PatientService from "../../services/patientService";

interface MedicalService {
  id: number;
  name: string;
  description?: string;
  base_fee: number;
  clinic_percentage: number;
  provider_percentage: number;
  owner_percentage: number;
  referrer_percentage: number;
  service_type: {
    id: number;
    name: string;
    category: "consultation" | "machine" | "treatment";
    requires_equipment: boolean;
  };
  provider: {
    id: number;
    first_name: string;
    last_name: string;
    role: "doctor" | "technician";
  };
  equipment?: {
    id: number;
    name: string;
    type: string;
    status: string;
    owner: { id: number; first_name: string; last_name: string };
    operator?: { id: number; first_name: string; last_name: string };
  };
}

interface SelectedService {
  service: MedicalService;
  referrer_name: string;
  customPrice?: number;
  pricingReason?: string;
  isReadOnly?: boolean;
  setPricingBy?: string;
}

interface ServiceSelectionModalProps {
  isOpen: boolean;
  appointment: Appointment;
  mode?: "pricing" | "completion" | "editing"; // Added editing mode
  onClose: () => void;
  onComplete: (selectedServices: SelectedService[]) => Promise<void>;
  onSaveCustomPricing?: (
    appointmentId: number,
    selectedServices: SelectedService[]
  ) => Promise<void>;
  onEditCompleted?: (
    appointmentId: number,
    selectedServices: SelectedService[],
    password: string,
    editReason: string
  ) => Promise<void>; // New callback for editing completed appointments
  onMarkIncomplete?: (
    appointmentId: number,
    password: string,
    reason: string
  ) => Promise<void>; // New callback for marking incomplete
}

const ServiceSelectionModal: React.FC<ServiceSelectionModalProps> = ({
  isOpen,
  appointment,
  mode = "completion",
  onClose,
  onComplete,
  onSaveCustomPricing,
  onEditCompleted,
  onMarkIncomplete,
}) => {
  const { user } = useAuth();
  const [availableServices, setAvailableServices] = useState<MedicalService[]>(
    []
  );
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [referrerSuggestions, setReferrerSuggestions] = useState<string[]>([]);
  const [totalEditMode, setTotalEditMode] = useState(false);
  const [customTotal, setCustomTotal] = useState<number | null>(null);
  const [selectedDiscount, setSelectedDiscount] = useState<number>(0);
  const [totalAdjustmentReason, setTotalAdjustmentReason] = useState("");
  // New states for enhanced features
  const [searchQuery, setSearchQuery] = useState("");
  const [doctorFilter, setDoctorFilter] = useState("");
  const [availableDoctors, setAvailableDoctors] = useState<any[]>([]);
  const [password, setPassword] = useState("");
  const [editReason, setEditReason] = useState("");
  const [incompleteReason, setIncompleteReason] = useState("");

  // Responsive states
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const discountOptions = [
    { value: 0, label: "No Discount" },
    { value: 10, label: "10% Discount" },
    { value: 15, label: "15% Discount" },
    { value: 20, label: "20% Discount" },
    { value: 25, label: "25% Discount" },
  ];
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const API_BASE =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  // Load available doctors for filtering (admin/main secretary only)
  const loadAvailableDoctors = async () => {
    try {
      if (!user?.roles?.includes("admin") && !user?.is_main_secretary) {
        return;
      }

      // Use the same service as your main page
      const doctorsData = await PatientService.getDoctors();
      // console.log("Doctors from PatientService:", doctorsData); // Debug
      setAvailableDoctors(doctorsData);
    } catch (error) {
      console.error("Error loading doctors:", error);
      setAvailableDoctors([]); // Set empty array on error
    }
  };

  // Enhanced service loading with search and filters
  const loadAvailableServices = useCallback(async () => {
    try {
      setLoadingServices(true);
      setError(null);
      const token = localStorage.getItem("auth_token");

      const params = new URLSearchParams({
        paginate: "false",
      });

      if (searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }

      if (doctorFilter) {
        params.append("doctor_id", doctorFilter);
      }

      const response = await fetch(`${API_BASE}/medical-services?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // console.log("API Response:", data); // Debug log

      // Handle different response structures
      let services = [];

      if (Array.isArray(data)) {
        // Direct array response
        services = data;
      } else if (data.data && Array.isArray(data.data)) {
        // Paginated response with data property
        services = data.data;
      } else if (data.services && Array.isArray(data.services)) {
        // Response with services property
        services = data.services;
      } else {
        console.error("Unexpected API response structure:", data);
        throw new Error("Invalid response format from services API");
      }

      // Filter for active services only
      const activeServices = services.filter(
        (service: any) => service.is_active !== false
      );

      // console.log("Processed services:", activeServices); // Debug log
      setAvailableServices(activeServices);
    } catch (error) {
      console.error("Error loading services:", error);
      setError("Failed to load available services. Please try again.");
      setAvailableServices([]); // Set empty array on error
    } finally {
      setLoadingServices(false);
    }
  }, [searchQuery, doctorFilter]);

  // Real-time search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isOpen) {
        loadAvailableServices();
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, doctorFilter, isOpen, loadAvailableServices]);

  // Load initial data
  useEffect(() => {
    if (isOpen) {
      loadAvailableDoctors();
      loadReferrerSuggestions();
    }
  }, [isOpen, appointment]);

  // Load existing services for completion/editing mode
  useEffect(() => {
    if (isOpen && availableServices.length > 0) {
      if (mode === "completion" || mode === "editing") {
        loadExistingAppointmentServices();
      } else {
        preSelectServices(availableServices);
      }
    }
  }, [isOpen, mode, availableServices]);

  const loadReferrerSuggestions = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE}/revenue/transactions?per_page=50`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const referrers = data.data
          ?.map((transaction: any) => transaction.referrer_name)
          .filter((name: string) => name && name.trim())
          .filter(
            (name: string, index: number, self: string[]) =>
              self.indexOf(name) === index
          )
          .slice(0, 10);

        setReferrerSuggestions(referrers || []);
      }
    } catch (error) {
      console.error("Error loading referrer suggestions:", error);
    }
  };

  const loadExistingAppointmentServices = async () => {
    try {
      setLoadingServices(true);
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${API_BASE}/appointments/${appointment.id}/pricing`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.services && data.services.length > 0) {
          const serviceDetailsPromises = data.services.map(
            async (serviceData: any) => {
              try {
                const serviceResponse = await fetch(
                  `${API_BASE}/medical-services/${serviceData.service_id}`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );

                if (serviceResponse.ok) {
                  return await serviceResponse.json();
                }
                return null;
              } catch (error) {
                console.error(
                  `Error loading service ${serviceData.service_id}:`,
                  error
                );
                return null;
              }
            }
          );

          const serviceDetails = await Promise.all(serviceDetailsPromises);

          const preSelectedServices: SelectedService[] = data.services
            .map((serviceData: any, index: number) => {
              const fullServiceData = serviceDetails[index];

              if (!fullServiceData) {
                return {
                  service: {
                    id: serviceData.service_id,
                    name: serviceData.service_name || "Unknown Service",
                    base_fee: serviceData.original_price || 0,
                    clinic_percentage: 0,
                    provider_percentage: 0,
                    owner_percentage: 0,
                    referrer_percentage: 0,
                    provider: {
                      id: appointment.doctor_id,
                      first_name: appointment.doctor?.first_name || "Unknown",
                      last_name: appointment.doctor?.last_name || "Doctor",
                      role: "doctor",
                    },
                    service_type: {
                      id: 1,
                      name: "General",
                      category: "consultation",
                      requires_equipment: false,
                    },
                  },
                  referrer_name: serviceData.referrer_name || "",
                  customPrice: serviceData.is_custom_priced
                    ? serviceData.final_price
                    : undefined,
                  pricingReason: serviceData.pricing_notes || "",
                  isReadOnly: mode === "completion",
                  setPricingBy: serviceData.set_by_doctor_name || "Doctor",
                };
              }

              return {
                service: fullServiceData,
                referrer_name: serviceData.referrer_name || "",
                customPrice: serviceData.is_custom_priced
                  ? serviceData.final_price
                  : undefined,
                pricingReason: serviceData.pricing_notes || "",
                isReadOnly: mode === "completion",
                setPricingBy: serviceData.set_by_doctor_name || "Doctor",
              };
            })
            .filter(Boolean);

          setSelectedServices(preSelectedServices);
        } else {
          setSelectedServices([]);
        }
      } else {
        setSelectedServices([]);
      }
    } catch (error) {
      console.error("Error loading existing appointment services:", error);
      setSelectedServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const preSelectServices = (services: MedicalService[]) => {
    if (mode === "completion" || mode === "editing") {
      return;
    }

    const selected: SelectedService[] = [];

    if (appointment.doctor_id) {
      const doctorServices = services.filter(
        (service) =>
          service.provider.id === appointment.doctor_id &&
          service.service_type.category === "consultation"
      );

      if (doctorServices.length > 0) {
        const basicConsultation =
          doctorServices.find(
            (s) =>
              s.name.toLowerCase().includes("general") ||
              s.name.toLowerCase().includes("consultation")
          ) || doctorServices[0];

        selected.push({
          service: basicConsultation,
          referrer_name: "",
        });
      }
    }

    setSelectedServices(selected);
  };

  // Handle mark incomplete
  const handleMarkIncomplete = async () => {
    if (!password.trim()) {
      setError("Password is required to mark appointment as incomplete");
      return;
    }

    if (!onMarkIncomplete) {
      setError("Mark incomplete function not available");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onMarkIncomplete(appointment.id, password, incompleteReason);
      onClose();
    } catch (error) {
      console.error("Error marking appointment as incomplete:", error);
      setError(
        "Failed to mark appointment as incomplete. Check your password."
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle edit completed appointment
  const handleEditCompleted = async () => {
    if (selectedServices.length === 0) {
      setError("Please select at least one service");
      return;
    }

    if (!password.trim()) {
      setError("Password is required to edit completed appointment");
      return;
    }

    if (!editReason.trim()) {
      setError("Edit reason is required");
      return;
    }

    if (!onEditCompleted) {
      setError("Edit completed function not available");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check if total adjustment was made
      if (customTotal !== null || selectedDiscount > 0) {
        // First apply the total price adjustment
        const adjustmentData = {
          total_amount: calculateTotalFee(),
          adjustment_type: (customTotal !== null
            ? "custom_total"
            : "discount") as "custom_total" | "discount",
          discount_percentage: selectedDiscount || undefined,
          adjustment_reason: totalAdjustmentReason || editReason,
          services: selectedServices.map((s) => ({
            service_id: s.service.id,
            adjusted_price: s.customPrice || s.service.base_fee,
          })),
        };

        await AppointmentService.applyTotalPriceAdjustment(
          appointment.id,
          adjustmentData
        );
      }

      // Then call the edit completed function
      await onEditCompleted(
        appointment.id,
        selectedServices,
        password,
        editReason
      );
      onClose();
    } catch (error) {
      console.error("Error editing completed appointment:", error);
      setError("Failed to edit appointment. Check your password.");
    } finally {
      setLoading(false);
    }
  };

  // Existing handlers...
  const handleServiceToggle = (service: MedicalService) => {
    setSelectedServices((prev) => {
      const existingIndex = prev.findIndex((s) => s.service.id === service.id);

      if (existingIndex >= 0) {
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        if (prev.length >= 5) {
          setError(
            "Warning: More than 5 services selected. Consider splitting into multiple appointments."
          );
        }
        return [
          ...prev,
          {
            service,
            referrer_name: "",
            customPrice: undefined,
            pricingReason: "",
          },
        ];
      }
    });
  };

  const handleCustomPriceChange = (serviceId: number, customPrice: string) => {
    setSelectedServices((prev) =>
      prev.map((selected) =>
        selected.service.id === serviceId
          ? {
              ...selected,
              customPrice: customPrice ? parseFloat(customPrice) : undefined,
            }
          : selected
      )
    );
  };

  const handlePricingReasonChange = (serviceId: number, reason: string) => {
    setSelectedServices((prev) =>
      prev.map((selected) =>
        selected.service.id === serviceId
          ? { ...selected, pricingReason: reason }
          : selected
      )
    );
  };

  const handleReferrerChange = (serviceId: number, referrerName: string) => {
    setSelectedServices((prev) =>
      prev.map((selected) =>
        selected.service.id === serviceId
          ? { ...selected, referrer_name: referrerName }
          : selected
      )
    );
  };

  const calculateTotalFee = (): number => {
    if (customTotal !== null) {
      return customTotal;
    }

    // If discount is applied, calculate from ORIGINAL prices, not custom prices
    if (selectedDiscount > 0) {
      const originalTotal = selectedServices.reduce((total, selected) => {
        return total + (Number(selected.service.base_fee) || 0);
      }, 0);
      return originalTotal * (1 - selectedDiscount / 100);
    }

    // Otherwise, use custom prices if available, or original prices
    const currentTotal = selectedServices.reduce((total, selected) => {
      const fee =
        selected.customPrice !== undefined
          ? Number(selected.customPrice)
          : Number(selected.service.base_fee) || 0;
      return total + (isNaN(fee) ? 0 : fee);
    }, 0);

    return currentTotal;
  };
  // Add function to handle total changes
  const handleTotalChange = (newTotal: number | null, discount: number = 0) => {
    if (newTotal !== null) {
      setCustomTotal(newTotal);
      setSelectedDiscount(0);
      distributeTotalAcrossServices(newTotal);
    } else if (discount > 0) {
      setSelectedDiscount(discount);
      setCustomTotal(null);

      // Clear existing custom prices and apply discount to original prices
      const originalTotal = selectedServices.reduce(
        (sum, s) => sum + Number(s.service.base_fee),
        0
      );
      const discountedTotal = originalTotal * (1 - discount / 100);

      // Reset all services to original prices first, then apply discount
      setSelectedServices((prev) =>
        prev.map((selected) => ({
          ...selected,
          customPrice: Number(
            (Number(selected.service.base_fee) * (1 - discount / 100)).toFixed(
              2
            )
          ),
          pricingReason: `${discount}% discount applied`,
        }))
      );
    } else {
      setCustomTotal(null);
      setSelectedDiscount(0);
      resetAllServicesToOriginal();
    }
  };

  // Add function to distribute total across services
  const distributeTotalAcrossServices = (newTotal: number) => {
    const originalTotal = selectedServices.reduce(
      (sum, s) => sum + Number(s.service.base_fee),
      0
    );

    if (originalTotal === 0) return;

    const ratio = newTotal / originalTotal;

    setSelectedServices((prev) =>
      prev.map((selected) => ({
        ...selected,
        customPrice: Number(
          (Number(selected.service.base_fee) * ratio).toFixed(2)
        ),
        pricingReason:
          totalAdjustmentReason ||
          (selectedDiscount > 0
            ? `${selectedDiscount}% discount applied`
            : "Total price adjustment"),
      }))
    );
  };

  // Add this function after the distributeTotalAcrossServices function
  const resetAllServicesToOriginal = () => {
    setSelectedServices((prev) =>
      prev.map((selected) => ({
        ...selected,
        customPrice: undefined,
        pricingReason: "",
      }))
    );
  };
  // Existing handlers for completion, save pricing, etc.
  const handleComplete = async () => {
    if (selectedServices.length === 0) {
      setError(
        "Please select at least one service or choose 'Complete without services'"
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onComplete(selectedServices);
    } catch (error) {
      console.error("Error completing appointment:", error);
      setError("Failed to complete appointment with services");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteWithoutServices = async () => {
    setLoading(true);
    setError(null);

    try {
      await onComplete([]);
    } catch (error) {
      console.error("Error completing appointment:", error);
      setError("Failed to complete appointment");
    } finally {
      setLoading(false);
    }
  };

  // Update handleSavePricing function
  const handleSavePricing = async () => {
    if (selectedServices.length === 0) {
      setError("Please select at least one service to set pricing");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // If total adjustment was made, use the new endpoint
      if (customTotal !== null || selectedDiscount > 0) {
        const adjustmentData = {
          total_amount: calculateTotalFee(),
          adjustment_type: (customTotal !== null
            ? "custom_total"
            : "discount") as "custom_total" | "discount",
          discount_percentage: selectedDiscount || undefined,
          adjustment_reason: totalAdjustmentReason,
          services: selectedServices.map((s) => ({
            service_id: s.service.id,
            adjusted_price: s.customPrice || s.service.base_fee,
          })),
        };

        await AppointmentService.applyTotalPriceAdjustment(
          appointment.id,
          adjustmentData
        );
      } else {
        // Use existing custom pricing endpoint
        if (onSaveCustomPricing) {
          await onSaveCustomPricing(appointment.id, selectedServices);
        }
      }

      onClose();
    } catch (error) {
      console.error("Error saving pricing:", error);
      setError("Failed to save pricing");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Determine if user can filter by doctor
  const canFilterByDoctor =
    user?.roles?.includes("admin") || user?.is_main_secretary;

  // Filtered services based on search and doctor filter
  const filteredServices = Array.isArray(availableServices)
    ? availableServices.filter((service) => {
        const matchesSearch =
          !searchQuery.trim() ||
          service.name.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesDoctor =
          !doctorFilter || service.provider.id.toString() === doctorFilter;

        return matchesSearch && matchesDoctor;
      })
    : [];

  // Modal header text based on mode
  const getHeaderText = () => {
    switch (mode) {
      case "pricing":
        return "Set Custom Price";
      case "editing":
        return "Edit Completed Appointment";
      default:
        return "Complete Appointment";
    }
  };

  // Responsive Styles (existing styles remain the same...)
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: isMobile ? "flex-end" : "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: isMobile ? "0" : "20px",
  };

  const modalStyle: React.CSSProperties = {
    background: colorTheme.cardBg,
    borderRadius: isMobile ? "16px 16px 0 0" : "16px",
    maxWidth: isMobile ? "100%" : isTablet ? "90%" : "900px",
    width: "100%",
    maxHeight: isMobile ? "95vh" : "90vh",
    overflow: "hidden",
    boxShadow: isMobile
      ? "0 -10px 25px rgba(0,0,0,0.15)"
      : "0 25px 50px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    animation: isMobile ? "slideUp 0.3s ease-out" : "none",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "20px 24px 16px 24px" : "24px 32px",
    borderBottom: `1px solid ${colorTheme.gray200}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: "auto",
    padding: isMobile ? "20px 24px" : "32px",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "12px" : "10px 12px",
    border: `2px solid ${colorTheme.gray200}`,
    borderRadius: "8px",
    fontSize: "14px",
    minHeight: isMobile ? "44px" : "auto",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "success" | "danger" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { bg: colorTheme.primary, text: "white" },
      secondary: { bg: colorTheme.gray500, text: "white" },
      success: { bg: colorTheme.success, text: "white" },
      danger: { bg: colorTheme.error, text: "white" },
    };

    const colors = variants[variant];

    return {
      padding: isMobile ? "14px 20px" : "12px 20px",
      border: "none",
      borderRadius: "8px",
      background: colors.bg,
      color: colors.text,
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s ease",
      minHeight: isMobile ? "48px" : "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };
  };

  return (
    <>
      <div style={overlayStyle}>
        <div style={modalStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <div>
              <h3
                style={{
                  fontSize: isMobile ? "18px" : "20px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  margin: "0 0 4px 0",
                }}
              >
                {getHeaderText()}
              </h3>
              <p
                style={{
                  color: colorTheme.gray500,
                  fontSize: isMobile ? "13px" : "14px",
                  margin: 0,
                }}
              >
                Patient: {appointment.patient.first_name}{" "}
                {appointment.patient.last_name} {getIcon("stethoscope")} Dr.{" "}
                {appointment.doctor.first_name} {appointment.doctor.last_name}
                {appointment.status === "completed" && (
                  <span
                    style={{ color: colorTheme.success, fontWeight: "500" }}
                  >
                    {getIcon("completed")} Completed
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                width: isMobile ? "36px" : "32px",
                height: isMobile ? "36px" : "32px",
                borderRadius: "50%",
                border: "none",
                background: colorTheme.gray100,
                color: colorTheme.gray500,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isMobile ? "18px" : "16px",
              }}
            >
              {getIcon("close")}
            </button>
          </div>

          {/* Content */}
          <div style={contentStyle}>
            {/* Search and Filter Section */}
            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: canFilterByDoctor
                    ? isMobile
                      ? "1fr"
                      : "2fr 1fr"
                    : "1fr",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                {/* Service Search */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "500",
                      color: colorTheme.gray700,
                      marginBottom: "6px",
                    }}
                  >
                    Search Services
                  </label>
                  <input
                    type="text"
                    id="search_service"
                    name="search_service"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by service name..."
                    style={inputStyle}
                  />
                </div>

                {/* Doctor Filter (for admin/main secretary) */}
                {canFilterByDoctor && (
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: colorTheme.gray700,
                        marginBottom: "6px",
                      }}
                    >
                      Filter by Doctor
                    </label>
                    <select
                      value={doctorFilter}
                      onChange={(e) => setDoctorFilter(e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">All Doctors</option>
                      {availableDoctors.map((doctor) => (
                        <option key={doctor.id} value={doctor.id}>
                          {doctor.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Results count */}
              {searchQuery && (
                <p
                  style={{
                    fontSize: "12px",
                    color: colorTheme.gray500,
                    margin: "0 0 8px 0",
                  }}
                >
                  {filteredServices.length} service
                  {filteredServices.length !== 1 ? "s" : ""} found
                </p>
              )}
            </div>

            {loadingServices ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <div
                  style={{
                    width: isMobile ? "30px" : "40px",
                    height: isMobile ? "30px" : "40px",
                    border: `4px solid ${colorTheme.gray200}`,
                    borderTop: `4px solid ${colorTheme.primary}`,
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 16px auto",
                  }}
                />
                <p>Loading available services...</p>
              </div>
            ) : (
              <>
                {/* Available Services */}
                <div style={{ marginBottom: "32px" }}>
                  <h4
                    style={{
                      fontSize: isMobile ? "15px" : "16px",
                      fontWeight: "600",
                      color: colorTheme.gray900,
                      marginBottom: "16px",
                    }}
                  >
                    Available Services ({filteredServices.length})
                  </h4>

                  {filteredServices.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: colorTheme.gray500,
                      }}
                    >
                      <p>
                        {searchQuery || doctorFilter
                          ? "No services match your search criteria."
                          : "No services available. Please add services first."}
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : isTablet
                          ? "repeat(auto-fill, minmax(280px, 1fr))"
                          : "repeat(auto-fill, minmax(300px, 1fr))",
                        gap: isMobile ? "12px" : "16px",
                      }}
                    >
                      {filteredServices.map((service) => {
                        const isSelected = selectedServices.some(
                          (s) => s.service.id === service.id
                        );

                        return (
                          <div
                            key={service.id}
                            onClick={() => handleServiceToggle(service)}
                            style={{
                              padding: isMobile ? "14px" : "16px",
                              border: `2px solid ${
                                isSelected
                                  ? colorTheme.primary
                                  : colorTheme.gray200
                              }`,
                              borderRadius: "12px",
                              cursor: "pointer",
                              background: isSelected
                                ? `${colorTheme.primary}10`
                                : colorTheme.cardBg,
                              transition: "all 0.2s ease",
                              minHeight: isMobile ? "auto" : "120px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: "8px",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <h5
                                  style={{
                                    fontSize: isMobile ? "13px" : "14px",
                                    fontWeight: "600",
                                    color: colorTheme.gray900,
                                    margin: "0 0 4px 0",
                                  }}
                                >
                                  {service.name}
                                </h5>
                                <p
                                  style={{
                                    fontSize: isMobile ? "11px" : "12px",
                                    color: colorTheme.gray600,
                                    margin: "0 0 8px 0",
                                  }}
                                >
                                  {service.provider.first_name}{" "}
                                  {service.provider.last_name} (
                                  {service.provider.role})
                                </p>
                              </div>
                              <div
                                style={{
                                  fontSize: isMobile ? "15px" : "16px",
                                  fontWeight: "700",
                                  color: isSelected
                                    ? colorTheme.primary
                                    : colorTheme.gray900,
                                }}
                              >
                                ${Number(service.base_fee) || 0}
                              </div>
                            </div>

                            {service.equipment && (
                              <div
                                style={{
                                  fontSize: isMobile ? "11px" : "12px",
                                  color: colorTheme.gray500,
                                  marginBottom: "8px",
                                }}
                              >
                                {getIcon("email")} {service.equipment.name}
                                {service.equipment.operator &&
                                  ` (Op: ${service.equipment.operator.first_name})`}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Selected Services */}
                {selectedServices.length > 0 && (
                  <div style={{ marginBottom: "32px" }}>
                    <h4
                      style={{
                        fontSize: isMobile ? "15px" : "16px",
                        fontWeight: "600",
                        color: colorTheme.gray900,
                        marginBottom: "16px",
                      }}
                    >
                      Selected Services ({selectedServices.length})
                    </h4>

                    {selectedServices.map((selected, index) => (
                      <div
                        key={selected.service.id}
                        style={{
                          padding: isMobile ? "16px" : "20px",
                          border: `1px solid ${colorTheme.gray200}`,
                          borderRadius: "12px",
                          marginBottom: "16px",
                          background: colorTheme.gray50,
                        }}
                      >
                        {/* Service Header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "16px",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <h5
                              style={{
                                fontSize: isMobile ? "14px" : "15px",
                                fontWeight: "600",
                                color: colorTheme.gray900,
                                margin: "0 0 4px 0",
                              }}
                            >
                              {selected.service.name}
                            </h5>
                            <p
                              style={{
                                fontSize: isMobile ? "12px" : "13px",
                                color: colorTheme.gray600,
                                margin: "0",
                              }}
                            >
                              {selected.service.provider?.first_name ||
                                "Unknown"}{" "}
                              {selected.service.provider?.last_name ||
                                "Provider"}
                            </p>
                            {selected.isReadOnly && selected.setPricingBy && (
                              <p
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  fontStyle: "italic",
                                  marginTop: "2px",
                                }}
                              >
                                Custom pricing set by {selected.setPricingBy}
                              </p>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: isMobile ? "15px" : "16px",
                              fontWeight: "700",
                              color: colorTheme.gray900,
                            }}
                          >
                            ${Number(selected.service.base_fee) || 0}
                          </div>
                        </div>

                        {/* Referrer Input */}
                        <div style={{ marginBottom: "16px" }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: colorTheme.gray700,
                              marginBottom: "6px",
                            }}
                          >
                            Sender (Optional)
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              id={`referrer_name_${selected.service.id}`}
                              name={`referrer_name_${selected.service.id}`}
                              value={selected.referrer_name}
                              onChange={(e) =>
                                handleReferrerChange(
                                  selected.service.id,
                                  e.target.value
                                )
                              }
                              placeholder="Enter referrer name..."
                              style={inputStyle}
                              disabled={
                                selected.isReadOnly && mode === "completion"
                              }
                              list={`referrer-suggestions-${selected.service.id}`}
                            />

                            <datalist
                              id={`referrer-suggestions-${selected.service.id}`}
                            >
                              {referrerSuggestions.map((suggestion, idx) => (
                                <option key={idx} value={suggestion} />
                              ))}
                            </datalist>
                          </div>
                        </div>

                        {/* Custom Pricing Section */}
                        {mode === "pricing" || mode === "editing" ? (
                          <div style={{ marginBottom: "16px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: colorTheme.gray700,
                                marginBottom: "6px",
                              }}
                            >
                              Custom Price (Optional) - Original: $
                              {Number(selected.service.base_fee) || 0}
                            </label>
                            <input
                              type="number"
                              id={`custom_price_${selected.service.id}`}
                              name={`custom_price_${selected.service.id}`}
                              value={selected.customPrice || ""}
                              onChange={(e) =>
                                handleCustomPriceChange(
                                  selected.service.id,
                                  e.target.value
                                )
                              }
                              placeholder={`Default: $${
                                Number(selected.service.base_fee) || 0
                              }`}
                              style={inputStyle}
                              min="0"
                              step="0.01"
                            />

                            {/* Pricing Reason for doctors */}
                            {selected.customPrice !== undefined && (
                              <div style={{ marginTop: "8px" }}>
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: colorTheme.gray700,
                                    marginBottom: "6px",
                                  }}
                                >
                                  Pricing Reason (Optional)
                                </label>
                                <input
                                  type="text"
                                  id={`pricing_reason_${selected.service.id}`}
                                  name={`pricing_reason_${selected.service.id}`}
                                  value={selected.pricingReason || ""}
                                  onChange={(e) =>
                                    handlePricingReasonChange(
                                      selected.service.id,
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., Financial hardship, Special discount..."
                                  style={inputStyle}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          // COMPLETION MODE: Show read-only pricing info
                          selected.customPrice !== undefined && (
                            <div
                              style={{
                                marginBottom: "16px",
                                padding: "12px",
                                background: "#fef3c7",
                                border: "1px solid #fbbf24",
                                borderRadius: "6px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  color: "#92400e",
                                  marginBottom: "4px",
                                }}
                              >
                                {getIcon("payment")} Custom Pricing Applied
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#92400e" }}
                              >
                                Original Price: $
                                {Number(selected.service.base_fee)}
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#92400e" }}
                              >
                                Custom Price: ${selected.customPrice}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#059669",
                                  fontWeight: "500",
                                }}
                              >
                                Patient Saves: $
                                {(
                                  Number(selected.service.base_fee) -
                                  selected.customPrice
                                ).toFixed(2)}
                              </div>
                              {selected.setPricingBy && (
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#6b7280",
                                    marginTop: "4px",
                                  }}
                                >
                                  Set by: {selected.setPricingBy}
                                </div>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Password and Reason Section for Post-Completion Editing */}
                {/* {(mode === "editing" ||
                  (appointment.status === "completed" &&
                    user?.roles?.includes("doctor"))) && (
                  <div style={{ marginBottom: "32px" }}>
                    <div
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: "8px",
                        padding: "16px",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#991b1b",
                          marginBottom: "8px",
                        }}
                      >
                        Security Verification Required
                      </div>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#7f1d1d",
                          margin: "0",
                        }}
                      >
                        Editing completed appointments requires password
                        verification for security audit purposes.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: colorTheme.gray700,
                          marginBottom: "6px",
                        }}
                      >
                        Your Password *
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your account password"
                        style={inputStyle}
                        required
                      />
                    </div>

                    
                    {appointment.status === "completed" && (
                      <div
                        style={{
                          borderTop: `1px solid ${colorTheme.gray200}`,
                          paddingTop: "16px",
                          marginTop: "16px",
                        }}
                      >
                        <h5
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: colorTheme.gray900,
                            marginBottom: "8px",
                          }}
                        >
                          Mark as Incomplete
                        </h5>
                        <p
                          style={{
                            fontSize: "12px",
                            color: colorTheme.gray600,
                            marginBottom: "12px",
                          }}
                        >
                          Alternatively, you can mark this appointment as
                          incomplete to allow normal editing.
                        </p>

                        <div style={{ marginBottom: "16px" }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: colorTheme.gray700,
                              marginBottom: "6px",
                            }}
                          >
                            Reason for Marking Incomplete (Optional)
                          </label>
                          <input
                            type="text"
                            value={incompleteReason}
                            onChange={(e) =>
                              setIncompleteReason(e.target.value)
                            }
                            placeholder="e.g., Need to add additional services, billing error..."
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )} */}
                {/* Move this section right after the "Selected Services" section and BEFORE the "Password and Reason Section" */}

                {/* Total Price Editor */}
                {selectedServices.length > 0 &&
                  (mode === "pricing" ||
                    mode === "completion" ||
                    mode === "editing") && (
                    <div
                      style={{
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "24px", // Add margin bottom
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          marginBottom: "16px",
                        }}
                      >
                        Total Price Options
                      </h4>

                      {/* Discount Buttons */}
                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          Quick Discounts
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                          }}
                        >
                          {discountOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                handleTotalChange(null, option.value)
                              }
                              style={{
                                padding: "8px 12px",
                                fontSize: "13px",
                                border:
                                  selectedDiscount === option.value
                                    ? "2px solid #3b82f6"
                                    : "1px solid #d1d5db",
                                borderRadius: "6px",
                                background:
                                  selectedDiscount === option.value
                                    ? "#eff6ff"
                                    : "#ffffff",
                                color:
                                  selectedDiscount === option.value
                                    ? "#1e40af"
                                    : "#374151",
                                cursor: "pointer",
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Custom Total Input */}
                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            marginBottom: "8px",
                            display: "block",
                          }}
                        >
                          Or Set Custom Total
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="number"
                            id="custom_total"
                            name="custom_total"
                            value={customTotal || ""}
                            onChange={(e) => {
                              const value = e.target.value
                                ? parseFloat(e.target.value)
                                : null;
                              setCustomTotal(value);
                            }}
                            onBlur={() => {
                              if (customTotal !== null) {
                                handleTotalChange(customTotal);
                              }
                            }}
                            placeholder="Enter total amount"
                            style={{
                              padding: "10px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "14px",
                              width: "150px",
                            }}
                            step="0.01"
                            min="0"
                          />

                          <button
                            type="button"
                            onClick={() => handleTotalChange(null, 0)}
                            style={{
                              padding: "8px 12px",
                              fontSize: "13px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              background: "#ffffff",
                              cursor: "pointer",
                            }}
                          >
                            Reset
                          </button>
                        </div>
                      </div>

                      {/* Adjustment Reason */}
                      {(customTotal !== null || selectedDiscount > 0) && (
                        <div>
                          <label
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              marginBottom: "8px",
                              display: "block",
                            }}
                          >
                            Reason for Price Adjustment
                          </label>
                          <input
                            type="text"
                            id="total_adjustment_reason"
                            name="total_adjustment_reason"
                            value={totalAdjustmentReason}
                            onChange={(e) =>
                              setTotalAdjustmentReason(e.target.value)
                            }
                            placeholder="e.g., Financial hardship, Bulk discount..."
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              border: "1px solid #d1d5db",
                              borderRadius: "6px",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                {/* Password and Edit Reason Section (for editing mode) */}
                {mode === "editing" && (
                  <div style={{ marginBottom: "32px" }}>
                    <div
                      style={{
                        padding: "20px",
                        background: "#fef3c7",
                        border: "1px solid #fbbf24",
                        borderRadius: "12px",
                        marginBottom: "16px",
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#92400e",
                          marginBottom: "16px",
                        }}
                      >
                        {getIcon("warning")} Editing Completed Appointment
                      </h4>

                      <div style={{ marginBottom: "16px" }}>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#92400e",
                            marginBottom: "6px",
                          }}
                        >
                          Your Password (Required)
                        </label>
                        <input
                          type="password"
                          id="account_password"
                          name="account_password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter your account password..."
                          style={{
                            ...inputStyle,
                            borderColor: "#fbbf24",
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#92400e",
                            marginBottom: "6px",
                          }}
                        >
                          Reason for Editing (Required)
                        </label>
                        <textarea
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          placeholder="e.g., Incorrect service recorded, Patient requested additional service..."
                          style={{
                            ...inputStyle,
                            borderColor: "#fbbf24",
                            minHeight: "80px",
                            resize: "vertical",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Error Display */}
            {error && (
              <div
                style={{
                  padding: "12px 16px",
                  background: error.startsWith("Warning")
                    ? "#fef3c7"
                    : "#fecaca",
                  border: `1px solid ${
                    error.startsWith("Warning") ? "#fbbf24" : "#fca5a5"
                  }`,
                  borderRadius: "8px",
                  color: error.startsWith("Warning") ? "#92400e" : "#991b1b",
                  fontSize: "14px",
                  marginBottom: "24px",
                }}
              >
                {error}
              </div>
            )}
          </div>

          {/* Pricing Summary */}
          {selectedServices.length > 0 && (
            <div
              style={{
                background: colorTheme.gray50,
                borderRadius: "8px",
                padding: "16px",
                margin: "0 32px 16px 32px",
                border: `1px solid ${colorTheme.gray200}`,
              }}
            >
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  marginBottom: "12px",
                }}
              >
                Pricing Summary
              </h4>

              <div style={{ fontSize: "12px", marginBottom: "8px" }}>
                Original Total: $
                {selectedServices
                  .reduce(
                    (sum, s) => sum + (Number(s.service.base_fee) || 0),
                    0
                  )
                  .toFixed(2)}
              </div>

              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: colorTheme.primary,
                  marginBottom: "8px",
                }}
              >
                Patient Pays: ${calculateTotalFee().toFixed(2)}
              </div>

              {selectedServices.some((s) => s.customPrice !== undefined) && (
                <div
                  style={{
                    fontSize: "11px",
                    color: "#f59e0b",
                    fontStyle: "italic",
                  }}
                >
                  {getIcon("warning")} Custom pricing applied - verify amount
                  with patient
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div
            style={{
              padding: isMobile ? "20px 24px" : "24px 32px",
              borderTop: `1px solid ${colorTheme.gray200}`,
              background: colorTheme.gray50,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? "16px" : "12px",
            }}
          >
            {/* Total Summary */}
            <div style={{ order: isMobile ? 1 : 2 }}>
              <div
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "700",
                  color: colorTheme.gray900,
                }}
              >
                Patient Total: $
                {isNaN(calculateTotalFee())
                  ? "0.00"
                  : calculateTotalFee().toFixed(2)}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: colorTheme.gray500,
                }}
              >
                {selectedServices.length} service
                {selectedServices.length !== 1 ? "s" : ""} selected
              </div>
              {/* Show mode indicator */}
              <div
                style={{
                  fontSize: "11px",
                  color:
                    mode === "pricing"
                      ? "#f59e0b"
                      : mode === "editing"
                      ? "#dc2626"
                      : "#10b981",
                  fontWeight: "500",
                  marginTop: "2px",
                }}
              >
                {mode === "pricing"
                  ? `${getIcon("payment")} Setting Custom Price`
                  : mode === "editing"
                  ? `${getIcon("warning")} Editing Completed Appointment`
                  : `${getIcon("success")} Ready to Complete`}
              </div>
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                gap: "12px",
                order: isMobile ? 2 : 1,
              }}
            >
              {/* Cancel Button - Always available */}
              <button
                onClick={onClose}
                style={{
                  ...buttonStyle("secondary"),
                  background: colorTheme.cardBg,
                  color: colorTheme.gray600,
                  border: `1px solid ${colorTheme.gray200}`,
                  flex: isMobile ? "1" : "initial",
                }}
              >
                Cancel
              </button>

              {/* Mode-specific buttons */}
              {mode === "pricing" ? (
                // PRICING MODE - For Doctors
                <button
                  onClick={handleSavePricing}
                  disabled={loading || selectedServices.length === 0}
                  style={{
                    ...buttonStyle("primary"),
                    background: "#f59e0b", // Orange for pricing
                    opacity: loading || selectedServices.length === 0 ? 0.6 : 1,
                    flex: isMobile ? "1" : "initial",
                  }}
                >
                  {loading
                    ? "Saving..."
                    : `${getIcon("payment")} Save Custom Pricing`}{" "}
                </button>
              ) : mode === "editing" ? (
                // EDITING MODE - For post-completion editing
                <>
                  {/* Mark Incomplete Button */}
                  <button
                    onClick={handleMarkIncomplete}
                    disabled={loading || !password.trim()}
                    style={{
                      ...buttonStyle("secondary"),
                      background: "#f59e0b",
                      opacity: loading || !password.trim() ? 0.6 : 1,
                      flex: isMobile ? "1" : "initial",
                    }}
                  >
                    {loading ? "Processing..." : "Mark Incomplete"}
                  </button>

                  {/* Save Edits Button */}
                  <button
                    onClick={handleEditCompleted}
                    disabled={
                      loading ||
                      selectedServices.length === 0 ||
                      !password.trim() ||
                      !editReason.trim()
                    }
                    style={{
                      ...buttonStyle("danger"),
                      opacity:
                        loading ||
                        selectedServices.length === 0 ||
                        !password.trim() ||
                        !editReason.trim()
                          ? 0.6
                          : 1,
                      flex: isMobile ? "1" : "initial",
                    }}
                  >
                    {loading ? "Saving..." : `${getIcon("warning")} Save Edits`}{" "}
                  </button>
                </>
              ) : (
                // COMPLETION MODE - For Secretaries
                <>
                  <button
                    onClick={handleCompleteWithoutServices}
                    disabled={loading}
                    style={{
                      ...buttonStyle("secondary"),
                      opacity: loading ? 0.6 : 1,
                      flex: isMobile ? "1" : "initial",
                    }}
                  >
                    Complete Without Services
                  </button>

                  <button
                    onClick={handleComplete}
                    disabled={loading || selectedServices.length === 0}
                    style={{
                      ...buttonStyle("success"),
                      opacity:
                        loading || selectedServices.length === 0 ? 0.6 : 1,
                      flex: isMobile ? "1" : "initial",
                    }}
                  >
                    {loading
                      ? "Completing..."
                      : `${getIcon(
                          "payment"
                        )} Complete & Charge $${calculateTotalFee().toFixed(
                          2
                        )}`}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes slideUp {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
          
          /* Mobile scrollbar styling */
          @media (max-width: 768px) {
            .service-modal-content::-webkit-scrollbar {
              width: 4px;
            }
            
            .service-modal-content::-webkit-scrollbar-track {
              background: #f1f1f1;
            }
            
            .service-modal-content::-webkit-scrollbar-thumb {
              background: #c1c1c1;
              border-radius: 2px;
            }
          }
          
          /* Touch targets */
          @media (max-width: 768px) {
            button, input, select {
              min-height: 44px;
            }
            
            .service-card {
              min-height: 80px;
            }
          }
          
          /* Hover effects for desktop */
          @media (min-width: 769px) {
            .service-card:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }
            
            button:hover {
              transform: translateY(-1px);
            }
          }
          
          /* Focus styles for accessibility */
          input:focus, button:focus {
            outline: 2px solid #667eea;
            outline-offset: 2px;
          }
          
          /* Grid responsive behavior */
          @media (max-width: 480px) {
            .services-grid {
              grid-template-columns: 1fr !important;
            }
            
            .revenue-split-grid {
              grid-template-columns: repeat(2, 1fr) !important;
            }
          }
          
          /* Smooth animations */
          .service-card {
            transition: all 0.2s ease;
          }
          
          button {
            transition: all 0.2s ease;
          }
          
          .modal-enter {
            animation: slideUp 0.3s ease-out;
          }
        `}
      </style>
    </>
  );
};

export default ServiceSelectionModal;
